import { createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../utils/callApi'

export interface EmbedItem {
  _id: string
  characterId: string
  item: string
  order: number
}

export interface Weapon {
  _id: string
  user?: string // Old Weapon model
  walletAddress?: string // New CraftedWeapon model
  weaponNo?: number // Old Weapon model
  weaponId?: string // New CraftedWeapon model (e.g., "crimson_core")
  weaponType: 'sword' | 'spear' | 'armor' | 'shoe' | 'helmet' | 'basic' | 'epic' // Support both old and new types
  elemental?: 'fire' | 'water' | 'earth' | 'lightning' | 'physics' | 'magic' | 'sun' | 'dark'
  attribute?: string
  weaponName: string
  title?: string // Old Weapon model
  description: string
  weaponImg: string
  damageIncrease?: number
  armorIncrease?: number
  hpIncrease?: number
  specialEffects?: { [key: string]: any } // New CraftedWeapon model
  stock?: number
  availableStock?: number
  createdAt?: Date | string
  updatedAt?: Date | string
  /** Usage period (per-copy lifespan): every copy expires `expiresAt`; countdown runs from `acquiredAt`. */
  acquiredAt?: Date | string | null
  expiresAt?: Date | string | null
  lifespanSource?: string | null
}

export interface EmbedWeapon {
  _id: string
  characterId: string
  weaponId: string | Weapon
  weaponNo: number
  order: number
}

// ✨ Character Instance Interface (matching backend)
export interface CharacterInstance {
  _id: string
  walletAddress: string
  characterId: string
  rarity: 'common' | 'rare' | 'legendary'
  elemental?: 'fire' | 'water' | 'earth' | 'lightning' | 'physics' | 'magic' | 'sun' | 'dark'
  nickname?: string
  level: number
  experience: number
  energy: number
  abilities?: {
    water: number
    ecs: number
    attack: number
    health: number
    armor: number
  }
  stats: {
    maxHp: number
    maxSp: number
    attackPower: number
    criticalChance: number
    armor?: number
  }
  embedItems: EmbedItem[]
  embedWeapons?: EmbedWeapon[]
  lastUsedAt: Date
  createdAt: Date
}

// ✨ Get Character by ID Action
export const getCharacterById = createAsyncThunk<
  CharacterInstance,
  { characterInstanceId: string, walletAddress: string },
  { rejectValue: string }
>(
  'character/getCharacterById',
  async ({ characterInstanceId, walletAddress }, { rejectWithValue }) => {
    try {
      const response = await api('characters/fetch-character-by-id', 'post', {
        characterId: characterInstanceId,
        walletAddress: walletAddress,
      })
      
      if (response.success) {
        return response.data
      } else {
        return rejectWithValue(response.message || 'Failed to fetch character')
      }
    } catch (error) {
      return rejectWithValue('Network error while fetching character')
    }
  }
)

// ✨ Set Weapon to Character Action
export const setWeaponToCharacter = createAsyncThunk<
  { success: boolean; message: string },
  { 
    walletAddress: string
    characterInstanceId: string
    weaponId: string
  },
  { rejectValue: string }
>(
  'character/setWeaponToCharacter',
  async ({ walletAddress, characterInstanceId, weaponId }, { rejectWithValue }) => {
    try {
      const response = await api('characters/set-weapon', 'post', {
        walletAddress,
        characterId: characterInstanceId,
        weaponId
      })
      
      if (response.success) {
        return response
      } else {
        return rejectWithValue(response.message || 'Failed to set weapon to character')
      }
    } catch (error) {
      return rejectWithValue('Network error while setting weapon')
    }
  }
)

// ✨ Fetch Weapons Action
export const fetchWeapons = createAsyncThunk<
  any[],
  { walletAddress: string },
  { rejectValue: string }
>(
  'character/fetchWeapons',
  async ({ walletAddress }, { rejectWithValue }) => {
    try {
      const response = await api('characters/fetch-weapons', 'post', {
        walletAddress
      })
      
      if (response.success) {
        return response.data
      } else {
        return rejectWithValue(response.message || 'Failed to fetch weapons')
      }
    } catch (error) {
      return rejectWithValue('Network error while fetching weapons')
    }
  }
)

// ✨ Add Weapon to Character Action
export const addWeaponToCharacter = createAsyncThunk<
  { success: boolean; data: any },
  { 
    walletAddress: string
    weaponId: string
    characterId: string
    order: number
  },
  { rejectValue: string }
>(
  'character/addWeaponToCharacter',
  async ({ walletAddress, weaponId, characterId, order }, { rejectWithValue }) => {
    try {
      const response = await api('characters/add-weapon', 'post', {
        walletAddress,
        weaponId,
        characterId,
        order
      })
      
      if (response.success) {
        return response
      } else {
        return rejectWithValue(response.message || 'Failed to add weapon to character')
      }
    } catch (error) {
      return rejectWithValue('Network error while adding weapon')
    }
  }
)

// ✨ Remove Weapon from Character Action
export const removeWeaponFromCharacter = createAsyncThunk<
  { success: boolean },
  { 
    walletAddress: string
    characterId: string
    order: number
  },
  { rejectValue: string }
>(
  'character/removeWeaponFromCharacter',
  async ({ walletAddress, characterId, order }, { rejectWithValue }) => {
    try {
      const response = await api('characters/remove-weapon', 'post', {
        walletAddress,
        characterId,
        order
      })
      
      if (response.success) {
        return response
      } else {
        return rejectWithValue(response.message || 'Failed to remove weapon from character')
      }
    } catch (error) {
      return rejectWithValue('Network error while removing weapon')
    }
  }
)